import { useApolloClient } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReactElement } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { z } from "zod";

import {
  MetadataBoolFieldSchema,
  MetadataFieldTypes,
} from "@stw/yup-validators";

import {
  MetadataBoolFieldFragment,
  UpdateMetadataBoolFieldMutation,
  UpdateMetadataBoolFieldMutationVariables,
} from "~frontend/__generated__/graphql";
import { FieldForm } from "~frontend/components/campaignMetadata/FieldForm";
import { handlePostSubmit } from "~frontend/components/campaignMetadata/common";
import { RhfYesNoSelect } from "~frontend/components/forms/react-hook-form-components/RhfYesNoSelect";
import {
  UPDATE_METADATA_BOOL_FIELD,
  GET_TENANT_METADATA_FIELDS,
} from "~frontend/graphql/campaignMetadata";
import useMutation from "~frontend/util/useMutation";

type EditBoolFieldProps = {
  editField?: MetadataBoolFieldFragment;
  initialName: string;
  onTypeChange: (name: string, type: MetadataFieldTypes) => void;
  onCancelClick: () => void;
};

type FormInput = FieldValues & z.input<typeof MetadataBoolFieldSchema>;
type FormOutput = FieldValues & z.infer<typeof MetadataBoolFieldSchema>;

const BoolFieldForm = FieldForm<FormInput>;

export function EditBoolFieldForm({
  editField,
  onTypeChange,
  onCancelClick,
  initialName,
}: EditBoolFieldProps): ReactElement {
  const id = editField ? editField.id : undefined;
  const client = useApolloClient();
  const {
    control,
    handleSubmit,
    reset,
    setError,
    getValues,
    watch,
    formState: { isSubmitting },
  } = useForm({
    resolver: zodResolver(MetadataBoolFieldSchema),
    values: {
      id,
      name: initialName,
      required: editField?.required || false,
      defaultValue: editField?.defaultBoolValue ?? null,
    },
  });

  const [updateField] = useMutation<
    UpdateMetadataBoolFieldMutation,
    UpdateMetadataBoolFieldMutationVariables
  >(UPDATE_METADATA_BOOL_FIELD, {
    refetchQueries: [GET_TENANT_METADATA_FIELDS],
    awaitRefetchQueries: true,
  });

  const onSubmit = async (values: FormOutput): Promise<void> => {
    const { name, required, defaultValue } = values;
    const { errors, data } = await updateField({
      variables: {
        input: {
          id,
          name,
          required,
          defaultValue,
        },
      },
    });

    handlePostSubmit({
      onSuccess: onCancelClick,
      setError,
      apolloClient: client,
      isNewField: !editField,
      data: data?.createOrUpdateCampaignMetadataBoolField,
      errors,
    });
  };

  const onFormCancelClick = () => {
    reset();
    onCancelClick();
  };

  return (
    <BoolFieldForm
      editField={editField}
      getValues={getValues}
      watch={watch}
      onTypeChange={onTypeChange}
      currentFieldType={MetadataFieldTypes.BOOLEAN}
      onCancelClick={onFormCancelClick}
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit(onSubmit)}
      control={control}
    >
      <RhfYesNoSelect
        control={control}
        label="Default Value (Optional)"
        name="defaultValue"
      />
    </BoolFieldForm>
  );
}
